import { useState } from "react";
import { useNavigate } from "react-router";
import { SearchFilter } from "src/components/SearchFilter/SearchFilter";
import ChevronDown from "src/design-system/icons/ChevronDown.svg?react";
import ChevronUp from "src/design-system/icons/ChevronUp.svg?react";
import { Button } from "react-aria-components";
import { DebugFeatureInput } from "../../components/DebugWindow/DebugFeatureInput";
import {
  type FeatureName,
  PossibleFeaturesMap,
} from "../../feature/FeatureConfig";
import { setSearchParamsFeature } from "../../feature/internal/searchParams/useSearchParamsFeatures";
import { useFeatureConfig } from "../../feature/useFeatureConfig";
import { useLayout } from "../../utils/hooks/useLayout";
import { useTypedLocation } from "../../utils/hooks/useTypedLocation";
import { searchParamsChange } from "../../utils/location/searchParamsChange";

export function FrontendFeatureSelector() {
  const location = useTypedLocation();
  const featureConfig = useFeatureConfig();
  const navigate = useNavigate();
  const layout = useLayout();

  const featureKeys = Object.keys(PossibleFeaturesMap).sort();

  const [featuresKeysFiltered, setFeaturesKeysFiltered] = useState(featureKeys);

  const [isExpanded, setIsExpanded] = useState(false);

  const setFeature = (featureKey: FeatureName, valueIndex: number) => {
    const newSearchParams = setSearchParamsFeature(
      featureKey,
      PossibleFeaturesMap[featureKey][valueIndex],
      location.search,
    );

    /** Without this, on select of a feature, the screen will update to search.
     *  We also don't want to force this on desktop, as it updates the pane on
     *  the left.
     *  */
    if (layout === "mobile") {
      navigate(searchParamsChange(newSearchParams, location), {
        replace: true,
        state: {
          highlightedTab: "preferences",
          preferencesScreen: "debug",
        },
      });
    } else {
      navigate(searchParamsChange(newSearchParams, location), {
        replace: true,
      });
    }
  };

  const toggleExpand = () => {
    if (isExpanded) setIsExpanded(false);
    else setIsExpanded(true);
  };

  return (
    <>
      <Button
        className="flex w-full cursor-pointer items-center justify-between p-4 text-left text-sm 2xl:w-100"
        onPress={toggleExpand}
      >
        <h3>Frontend Features</h3>
        {!isExpanded ? <ChevronDown /> : <ChevronUp />}
      </Button>
      {isExpanded && (
        <>
          <div className="flex justify-end px-4 py-2">
            <SearchFilter
              data={featureKeys}
              onFilter={(filteredData) => setFeaturesKeysFiltered(filteredData)}
              filteringFunction={(item, filter) =>
                item.toLowerCase().includes(filter.toLowerCase())
              }
            />
          </div>
          {featuresKeysFiltered.map((key) => {
            const featureKey = key as FeatureName;
            const featureValue = PossibleFeaturesMap[featureKey].findIndex(
              (v) => v === featureConfig[featureKey],
            );

            return (
              <div
                className="flex w-full items-center justify-between p-4 text-sm 2xl:w-100"
                key={key}
              >
                {/* The title attribute is used to display a tooltip text when the mouse moves over the element. */}
                {/* So if the text is hidden by overflow, the tooltip will show the full text. */}
                <span
                  className="overflow-hidden overflow-ellipsis whitespace-nowrap"
                  title={key}
                >
                  {key}
                </span>
                <DebugFeatureInput
                  featureName={featureKey}
                  valueIndex={featureValue}
                  possibleFeatureValues={PossibleFeaturesMap[featureKey]}
                  setFeature={setFeature}
                />
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
